import { z } from 'zod';

import { NotificationChannelSchema } from './notification_channel';

/**
 * The rows of the traveler's notification grid (Projeto B1). Each row maps one or
 * more proactive notification types to the channels the person picked; the app
 * inbox is not a channel here — it always receives everything.
 */
export const NotificationPreferenceKeySchema = z.enum([
  'request_received',
  'approval',
  'trip_update',
  'requested_trip_update',
  'flight_reminder',
  'schedule_change',
  'flight_delay_gate',
  'flight_problem',
]);
export type NotificationPreferenceKey = z.infer<typeof NotificationPreferenceKeySchema>;
export const NOTIFICATION_PREFERENCE_KEYS = NotificationPreferenceKeySchema.options;

const ClockSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const QuietHoursSchema = z
  .object({ enabled: z.boolean(), start: ClockSchema, end: ClockSchema })
  .refine((quiet) => quiet.start !== quiet.end, {
    message: 'quiet hours must start and end at different times',
    path: ['end'],
  });
export type QuietHours = z.infer<typeof QuietHoursSchema>;

export const ReminderLeadMinutesSchema = z.union([z.literal(120), z.literal(180), z.literal(240)]);
export type ReminderLeadMinutes = z.infer<typeof ReminderLeadMinutesSchema>;

export const DEFAULT_QUIET_HOURS: QuietHours = { enabled: true, start: '22:00', end: '07:00' };

/**
 * The clocks the Notificações tab offers for the quiet window: whole hours, starting in the
 * evening and ending in the morning. customer-api accepts only these on a write; the schemas
 * above stay wider, so a window stored before still reads.
 */
export const QUIET_HOURS_START_OPTIONS = ['20:00', '21:00', '22:00', '23:00'] as const;
export const QUIET_HOURS_END_OPTIONS = ['05:00', '06:00', '07:00', '08:00', '09:00'] as const;
export const DEFAULT_REMINDER_LEAD_MINUTES = 180 as const satisfies ReminderLeadMinutes;
export const DEFAULT_QUIET_TIMEZONE = 'America/Sao_Paulo';

/** The company chat tools the platform integrates with, in the order views list them. */
export const ChatToolSchema = z.enum(['slack', 'teams']);
export type ChatTool = z.infer<typeof ChatToolSchema>;

/** The company chat as the person sees it: which tool, and whether they were found in it. */
export const ChatAvailabilitySchema = z
  .object({
    tool: ChatToolSchema.nullable(),
    state: z.enum(['found', 'no_workspace', 'not_in_workspace', 'deactivated']),
  })
  .refine((chat) => chat.tool !== null || chat.state === 'no_workspace', {
    message: 'tool can only be null when state is no_workspace',
    path: ['state'],
  });
export type ChatAvailability = z.infer<typeof ChatAvailabilitySchema>;

const ChannelListSchema = z
  .array(NotificationChannelSchema)
  .refine((channels) => new Set(channels).size === channels.length, {
    message: 'a channel appears once per row',
  });

export const NotificationPreferenceRowSchema = z.object({
  channels: ChannelListSchema,
  /** True while the person never touched the row: the channels are today's behaviour. */
  isDefault: z.boolean(),
});

/**
 * Every row of the grid, each one required. An object, not a `z.record` over the key enum: a
 * record refuses a key it does not know, so a row a newer customer-api adds (B2+) would fail the
 * whole view and its consumers would fall back to the defaults. The object strips that row.
 */
const NotificationPreferenceRowsSchema = z.object(
  Object.fromEntries(
    NOTIFICATION_PREFERENCE_KEYS.map((key) => [key, NotificationPreferenceRowSchema]),
  ) as Record<NotificationPreferenceKey, typeof NotificationPreferenceRowSchema>,
);

/** The resolved grid customer-api serves: defaults filled, filtered to what the person has. */
export const NotificationPreferencesViewSchema = z.object({
  byType: NotificationPreferenceRowsSchema,
  quietHours: z
    .object({
      enabled: z.boolean(),
      start: ClockSchema,
      end: ClockSchema,
      /** IANA zone the window is read in (the person's base, else Sao Paulo). */
      timezone: z.string().min(1),
      isDefault: z.boolean(),
    })
    .refine((quiet) => quiet.start !== quiet.end, {
      message: 'quiet hours must start and end at different times',
      path: ['end'],
    }),
  reminderLeadMinutes: ReminderLeadMinutesSchema,
  reminderLeadIsDefault: z.boolean(),
  /** Whether the issuance e-mail carries the trip's calendar events. `.default` reads an older customer-api as on. */
  addToCalendar: z.boolean().default(true),
  addToCalendarIsDefault: z.boolean().default(true),
  available: z.object({
    whatsapp: z.boolean(),
    email: z.boolean(),
    chat: ChatAvailabilitySchema,
  }),
});
export type NotificationPreferencesView = z.infer<typeof NotificationPreferencesViewSchema>;

/** One change from the Notificações tab; `reset` returns rows to their default. */
export const NotificationPreferencesPatchSchema = z
  .object({
    byType: z.partialRecord(NotificationPreferenceKeySchema, ChannelListSchema).optional(),
    quietHours: QuietHoursSchema.optional(),
    reminderLeadMinutes: ReminderLeadMinutesSchema.optional(),
    addToCalendar: z.boolean().optional(),
    reset: z.array(NotificationPreferenceKeySchema).optional(),
  })
  .refine(
    (patch) =>
      Object.keys(patch.byType ?? {}).length > 0 ||
      (patch.reset?.length ?? 0) > 0 ||
      patch.quietHours !== undefined ||
      patch.reminderLeadMinutes !== undefined ||
      patch.addToCalendar !== undefined,
    { message: 'the patch changes nothing' },
  )
  .refine(
    (patch) => {
      const resetKeys = patch.reset ?? [];
      const resetSet = new Set(resetKeys);
      // No overlap between set and reset
      const overlap = Object.keys(patch.byType ?? {}).some((key) =>
        resetSet.has(key as NotificationPreferenceKey),
      );
      if (overlap) return false;
      // No duplicates in reset
      if (resetSet.size !== resetKeys.length) return false;
      return true;
    },
    { message: 'a row is either set or reset, once' },
  );
export type NotificationPreferencesPatch = z.infer<typeof NotificationPreferencesPatchSchema>;
